<template>
  <div class="page-outer storefront-page-form-page">
    <div class="page-header page-header-fixed-form">
      <div class="header-left header-left--inline">
        <h1 class="page-title">
          <i class="ri-file-text-line"></i>
          <template v-if="isEdit">編輯頁面<span v-if="originalTitle">：{{ originalTitle }}</span></template>
          <template v-else>新增頁面</template>
        </h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" :disabled="saving || loading || !!loadError" @click="goBack">取消</button>
        <button type="button" class="btn btn-primary" :disabled="saving || loading || !!loadError" @click="savePage">
          <span v-if="saving" class="spinner-sm"></span>
          {{ saving ? '儲存中...' : (isEdit ? '儲存變更' : '建立頁面') }}
        </button>
      </div>
    </div>

    <div class="page-container">
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>載入中...</p>
      </div>

      <div v-else-if="loadError" class="error-state">
        <i class="ri-error-warning-line"></i> {{ loadError }}
        <button type="button" class="btn btn-secondary btn-sm" @click="loadPage">重新載入</button>
      </div>

      <div v-else class="form-container">
      <!-- 基本資訊 -->
      <div class="form-section">
        <h3 class="section-title">基本資訊</h3>
        <div class="form-group">
          <label class="form-label required">頁面標題</label>
          <input v-model="form.title" class="form-control" placeholder="例：關於我們" />
        </div>
        <div class="form-group">
          <label class="form-label">URL Slug</label>
          <input v-model="form.slug" class="form-control" placeholder="留空則自動由標題產生" />
          <FormTipBox>
            <p class="form-hint">
              <strong>用途：</strong>作為前台<strong>自訂 CMS 單頁</strong>的網址最後一段，訪客開啟路徑為 <code>/page/&lt;slug&gt;</code>。同一商店內各頁 Slug 必須唯一，且與後台「編輯／刪除」所帶的識別一致（勿隨意更動已上線的 Slug，以免舊連結失效）。
            </p>
            <p class="form-hint">
              <strong>留空時：</strong>儲存後由系統依「頁面標題」自動產生：與後端 <code>slugify</code> 一致，空白與常見分隔符合併為 <code>-</code>、移除 <code>? # / \\ %</code> 等不適合路徑的字元、拉丁字母轉小寫（中文等文字會保留）。若與既有頁面衝突，會自動改為 <code>slug-2</code>、<code>slug-3</code>… 直到唯一。
            </p>
            <p class="form-hint">
              <strong>自行填寫：</strong>建議使用中文、英數字與連字號 <code>-</code>；短而易記者較利於分享連結與辨識。
            </p>
            <p class="form-hint form-hint--example">
              <strong>設定範例：</strong><span class="seo-example-text">about-us、faq、shipping-policy（英文短碼）；或依品牌習慣自訂中文／中英混合片段。</span>
            </p>
            <p class="form-hint form-hint--preview">
              <strong>前台路徑預覽：</strong>
              <code class="slug-preview-code">/page/{{ previewPageSlug }}</code>
              <span class="form-hint-sub">完整網址為「商店前台網域」+ 上列路徑（以下為依目前輸入推估；留空時以上方標題預覽，儲存後若遇衝突微調可能略有差異）。</span>
            </p>
          </FormTipBox>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label class="form-label">狀態</label>
            <select v-model="form.status" class="form-control">
              <option value="active">上架</option>
              <option value="draft">草稿</option>
              <option value="inactive">下架</option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">排序</label>
            <input v-model.number="form.sortOrder" type="number" min="0" class="form-control" />
          </div>
        </div>
      </div>

      <!-- SEO -->
      <div class="form-section">
        <CommerceSeoMetaFields
          v-model:seo-title="form.seoTitle"
          v-model:seo-description="form.seoDescription"
          v-model:seo-keywords="form.seoKeywords"
          variant="storefrontPage"
        >
          <template #intro-extra>
            <FormTipBox spacing="section">
              <p class="form-hint form-hint--preview">
                <strong>適用頁面：</strong>本頁為<strong>自訂 CMS 單頁</strong>，前台路徑為
                <code class="slug-preview-code">/page/{{ seoPathSlug }}</code>
                <span class="form-hint-sub">（Slug 取自上方「URL Slug」；留空儲存後會依標題自動產生，與此處預覽可能略有差異。）</span>
              </p>
            </FormTipBox>
          </template>
        </CommerceSeoMetaFields>
      </div>

      <!-- 頁面模塊 -->
      <div class="form-section">
        <div class="section-header">
          <h3 class="section-title">頁面模塊（Blocks）</h3>
        </div>
        <p class="form-hint">依序組成頁面內容；可挑選下方支援的模塊類型新增。</p>

        <div v-if="!form.blocks.length" class="empty-hint">尚無模塊，點選下方新增。</div>

        <div v-for="(block, bi) in form.blocks" :key="bi" class="block-item">
          <div class="block-item-header">
            <span class="block-type-label">
              <i :class="BLOCK_TYPE_ICONS[block.type] || 'ri-layout-line'"></i>
              {{ BLOCK_TYPE_LABELS[block.type] ?? block.type }}
            </span>
            <div class="block-actions">
              <button type="button" class="btn-icon" :disabled="bi === 0" title="上移" @click="moveBlock(bi, -1)">
                <i class="ri-arrow-up-s-line"></i>
              </button>
              <button type="button" class="btn-icon" :disabled="bi === form.blocks.length - 1" title="下移" @click="moveBlock(bi, 1)">
                <i class="ri-arrow-down-s-line"></i>
              </button>
              <button type="button" class="btn-icon btn-icon--danger" title="刪除模塊" @click="removeBlock(bi)">
                <i class="ri-delete-bin-line"></i>
              </button>
            </div>
          </div>
          <div class="block-fields">
            <template v-if="block.type === 'richtext'">
              <div class="form-group">
                <label class="form-label-sm">內容（HTML）</label>
                <textarea v-model="block.props.html" class="form-control" rows="4" placeholder="<p>文字內容...</p>"></textarea>
              </div>
            </template>

            <template v-else-if="block.type === 'hero'">
              <div class="form-group">
                <label class="form-label-sm">標題</label>
                <input v-model="block.props.title" class="form-control" />
              </div>
              <div class="form-group">
                <label class="form-label-sm">副標題</label>
                <input v-model="block.props.subtitle" class="form-control" />
              </div>
              <div class="form-group">
                <label class="form-label-sm">背景圖片</label>
                <ImageUploader v-model="block.props.imageUrl" :uploadFn="uploadImage" shape="card" />
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label class="form-label-sm">按鈕文字</label>
                  <input v-model="block.props.ctaLabel" class="form-control" placeholder="立即購買" />
                </div>
                <div class="form-group">
                  <label class="form-label-sm">按鈕連結</label>
                  <input v-model="block.props.ctaHref" class="form-control" placeholder="https://..." />
                </div>
              </div>
            </template>

            <template v-else-if="block.type === 'image'">
              <div class="form-group">
                <label class="form-label-sm">圖片</label>
                <ImageUploader v-model="block.props.imageUrl" :uploadFn="uploadImage" shape="card" />
              </div>
              <div class="form-group">
                <label class="form-label-sm">說明文字</label>
                <input v-model="block.props.caption" class="form-control" placeholder="圖片說明（選填）" />
              </div>
            </template>

            <template v-else-if="block.type === 'cta'">
              <div class="form-group">
                <label class="form-label-sm">標題</label>
                <input v-model="block.props.title" class="form-control" />
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label class="form-label-sm">按鈕文字</label>
                  <input v-model="block.props.buttonLabel" class="form-control" placeholder="前往購物" />
                </div>
                <div class="form-group">
                  <label class="form-label-sm">按鈕連結</label>
                  <input v-model="block.props.buttonHref" class="form-control" placeholder="https://..." />
                </div>
              </div>
            </template>

            <template v-else-if="block.type === 'faq'">
              <div class="form-group">
                <label class="form-label-sm">標題</label>
                <input v-model="block.props.title" class="form-control" placeholder="常見問題" />
              </div>
              <div v-for="(q, qi) in (block.props.items || [])" :key="qi" class="faq-item">
                <input v-model="q.question" class="form-control form-control-sm" placeholder="問題" />
                <textarea v-model="q.answer" class="form-control form-control-sm" rows="2" placeholder="回答"></textarea>
                <button type="button" class="btn-icon btn-icon--danger" @click="block.props.items.splice(qi, 1)">
                  <i class="ri-close-line"></i>
                </button>
              </div>
              <button type="button" class="btn btn-secondary btn-sm mt-1" @click="addFaqItem(block)">
                <i class="ri-add-line"></i> 新增問答
              </button>
            </template>

            <template v-else>
              <div class="form-group">
                <label class="form-label-sm">自訂 Props（JSON）</label>
                <textarea v-model="block._propsJson" class="form-control form-control-sm" rows="4" @change="syncBlockProps(block)"></textarea>
              </div>
            </template>
          </div>
        </div>

        <div class="add-block-row">
          <select v-model="newBlockType" class="form-control form-control-sm add-block-select">
            <option v-for="bt in BLOCK_TYPES" :key="bt.value" :value="bt.value">{{ bt.label }}</option>
          </select>
          <button type="button" class="btn btn-secondary btn-sm" @click="addBlock">
            <i class="ri-add-line"></i> 新增模塊
          </button>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { usePageTitle } from '@/composables/usePageTitle.js'
import { commerceSettingsApi } from '@/api/modules/commerceSettings.js'
import { showError, showSuccess } from '@/utils/swal.js'
import ImageUploader from '@/components/ImageUploader.vue'
import FormTipBox from '@/components/form/FormTipBox.vue'
import CommerceSeoMetaFields from '@/components/commerce/CommerceSeoMetaFields.vue'
import apiClient from '@/api/axios.js'

const route = useRoute()
const router = useRouter()

const isEdit = computed(() => Boolean(route.params.slug))

/**
 * 與後端 commerceStorefrontService.slugify 一致，供預覽；實際儲存仍以前端送出 body 與後端去重為準。
 */
function slugifyPreview(text) {
  if (!text || typeof text !== 'string') return ''
  return text
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[?#/\\%]/g, '')
    .replace(/[\s\-–—_]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .trim()
}

/** URL Slug 提示區「前台路徑預覽」用片段 */
const previewPageSlug = computed(() => {
  const manual = form.value.slug?.trim()
  if (manual) {
    const s = slugifyPreview(manual)
    return s || '（請調整 Slug 內容）'
  }
  const title = form.value.title?.trim()
  if (title) {
    const s = slugifyPreview(title)
    return s || '（請手動填寫 Slug）'
  }
  return '（請先填寫頁面標題）'
})

/** SEO／提示區塊用的 Slug 預覽片段（與目前表單輸入一致，供 /page/ 路徑說明） */
const seoPathSlug = computed(() => {
  const s = form.value.slug?.trim()
  if (s) return slugifyPreview(s) || '（請調整 Slug）'
  const t = form.value.title?.trim()
  if (t) {
    const p = slugifyPreview(t)
    return p || '（自動產生）'
  }
  return '（自動產生）'
})

usePageTitle(isEdit.value ? '編輯前台頁面' : '新增前台頁面')

const BLOCK_TYPES = [
  { value: 'hero', label: 'Hero（大橫幅）' },
  { value: 'richtext', label: 'Rich Text（文字）' },
  { value: 'image', label: 'Image（圖片）' },
  { value: 'cta', label: 'CTA（行動呼籲）' },
  { value: 'faq', label: 'FAQ（問答）' },
]

const BLOCK_TYPE_LABELS = { hero: 'Hero', richtext: 'Rich Text', image: 'Image', cta: 'CTA', faq: 'FAQ' }
const BLOCK_TYPE_ICONS = {
  hero: 'ri-layout-top-line',
  richtext: 'ri-text',
  image: 'ri-image-line',
  cta: 'ri-cursor-line',
  faq: 'ri-question-answer-line'
}

const DEFAULT_BLOCK_PROPS = {
  hero: { title: '', subtitle: '', imageUrl: '', ctaLabel: '', ctaHref: '' },
  richtext: { html: '' },
  image: { imageUrl: '', caption: '' },
  cta: { title: '', buttonLabel: '', buttonHref: '' },
  faq: { title: '常見問題', items: [] },
}

const loading = ref(false)
const loadError = ref('')
const saving = ref(false)
const newBlockType = ref('richtext')
const originalSlug = ref('')
const originalTitle = ref('')
const form = ref({
  title: '',
  slug: '',
  status: 'active',
  sortOrder: 0,
  seoTitle: '',
  seoDescription: '',
  seoKeywords: '',
  blocks: [],
})

function goBack() {
  router.push('/client/commerce/pages')
}

async function loadPage() {
  if (!isEdit.value) return
  const slug = route.params.slug
  if (!slug) {
    loadError.value = '缺少頁面 slug'
    return
  }
  loading.value = true
  loadError.value = ''
  try {
    const res = await commerceSettingsApi.getPage(slug)
    const page = res?.data ?? {}
    originalSlug.value = page.slug ?? slug
    originalTitle.value = page.title ?? ''
    form.value = {
      title: page.title ?? '',
      slug: page.slug ?? '',
      status: page.status ?? 'active',
      sortOrder: page.sort_order ?? 0,
      seoTitle: page.seo_title ?? page.seoTitle ?? '',
      seoDescription: page.seo_description ?? page.seoDescription ?? '',
      seoKeywords: page.seo_keywords ?? page.seoKeywords ?? '',
      blocks: page.blocks ? JSON.parse(JSON.stringify(page.blocks)) : [],
    }
  } catch (e) {
    loadError.value = e?.response?.data?.message || '載入失敗'
  } finally {
    loading.value = false
  }
}

function addBlock() {
  const props = JSON.parse(JSON.stringify(DEFAULT_BLOCK_PROPS[newBlockType.value] ?? {}))
  form.value.blocks.push({ type: newBlockType.value, props })
}

function removeBlock(idx) {
  form.value.blocks.splice(idx, 1)
}

function moveBlock(idx, dir) {
  const arr = form.value.blocks
  const t = idx + dir
  if (t < 0 || t >= arr.length) return
  const tmp = arr[idx]
  arr[idx] = arr[t]
  arr[t] = tmp
}

function addFaqItem(block) {
  if (!block.props.items) block.props.items = []
  block.props.items.push({ question: '', answer: '' })
}

function syncBlockProps(block) {
  try {
    block.props = JSON.parse(block._propsJson)
  } catch {
    /* ignore */
  }
}

async function uploadImage(file) {
  const fd = new FormData()
  fd.append('file', file)
  return apiClient.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
}

async function savePage() {
  if (!form.value.title?.trim()) {
    await showError('請填寫頁面標題')
    return
  }
  saving.value = true
  try {
    if (isEdit.value) {
      if (!originalSlug.value) {
        await showError('找不到頁面識別')
        return
      }
      await commerceSettingsApi.updatePage(originalSlug.value, form.value)
      await showSuccess('頁面更新成功')
    } else {
      await commerceSettingsApi.createPage(form.value)
      await showSuccess('頁面建立成功')
    }
    router.push('/client/commerce/pages')
  } catch (e) {
    await showError(e?.response?.data?.message || '儲存失敗')
  } finally {
    saving.value = false
  }
}

onMounted(loadPage)
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.page-outer {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.page-header {
  margin-bottom: 0;
  background: #f5f7fa;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 10px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.header-left--inline {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.header-actions { display: flex; align-items: center; gap: 8px; }

.page-header .page-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.page-container {
  padding: 20px;
  padding-top: 72px;
}

.btn { padding: 6px 16px; border-radius: 6px; font-size: 13px; font-weight: 500; cursor: pointer; border: none; display: inline-flex; align-items: center; gap: 6px; }
.btn-primary { background: #3b82f6; color: #fff; }
.btn-primary:hover:not(:disabled) { background: #2563eb; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-secondary { background: #fff; color: #374151; border: 1px solid #d1d5db; }
.btn-secondary:hover:not(:disabled) { background: #f9fafb; }
.btn-secondary:disabled { opacity: 0.6; cursor: not-allowed; }

.spinner-sm {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: storefront-form-spin 0.7s linear infinite;
  flex-shrink: 0;
}

@keyframes storefront-form-spin {
  to { transform: rotate(360deg); }
}

.loading-state,
.error-state {
  background: #fff;
  border-radius: 12px;
  padding: 40px;
  text-align: center;
  color: #6b7280;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
}
.error-state { color: #b91c1c; }
.error-state .btn { margin-left: 10px; }

.form-container {
  max-width: 900px;
  margin: 0 auto;
  background: #fff;
  border-radius: 12px;
  padding: 28px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.form-section { margin-bottom: 32px; }
.form-section:last-child { margin-bottom: 0; }

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #222;
  margin: 0 0 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #f0f0f0;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.section-header .section-title { margin-bottom: 0; border: none; padding: 0; }

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.form-group { display: flex; flex-direction: column; margin-bottom: 16px; }
.form-group:last-child { margin-bottom: 0; }
.form-label { font-size: 13px; font-weight: 500; color: #555; margin-bottom: 5px; }
.form-label.required::after { content: ' *'; color: #e74c3c; }
.form-label-sm { font-size: 12px; color: #6b7280; margin-bottom: 3px; }
.form-hint { font-size: 12px; color: #6b7280; margin: 4px 0 0; }
.empty-hint { color: #9ca3af; font-size: 13px; padding: 8px 0; }

.form-control {
  padding: 8px 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
}
.form-control:focus {
  outline: none;
  border-color: #1a73e8;
  box-shadow: 0 0 0 2px rgba(26, 115, 232, 0.15);
}

.block-item {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 10px;
  overflow: hidden;
}
.block-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
}
.block-type-label {
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 6px;
}
.block-actions { display: flex; gap: 4px; }
.block-fields { padding: 12px; }

.faq-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
  background: #f9fafb;
  border-radius: 6px;
  margin-bottom: 6px;
}

.add-block-row {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed #e5e7eb;
}
.add-block-select { max-width: 200px; }

.btn-icon {
  background: none;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  padding: 2px 6px;
  cursor: pointer;
  font-size: 14px;
}
.btn-icon:disabled { opacity: 0.4; cursor: not-allowed; }
.btn-icon--danger { color: #ef4444; border-color: #fecaca; }
.btn-icon--danger:hover { background: #fff1f2; }

.mt-1 { margin-top: 4px; }

@media (max-width: 768px) {
  .page-container { padding: 12px; padding-top: 72px; }
  .form-container { padding: 16px; }
  .form-row { grid-template-columns: 1fr; }
}
</style>